import { FC, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input
} from "@libs/zapi-ui/shadcn";
import { AppButton, FigmaTypography } from "@libs/zapi-ui/custom";
import { AppLoader } from "../../shared/components/AppLoader";
import { getRoutePromptsFeature, getRoutePromptsRevision } from "../../shared/const/router";
import {
  captureTestCase,
  formatInputCount,
  listCaseBriefsForCapture,
  listTestCases,
  type CaseBriefPickerItem,
  type TestCaseListItem
} from "../../shared/services/promptTests";

const CaptureDialog: FC<{ open: boolean; onOpenChange: (open: boolean) => void; onCaptured: () => void }> = ({
  open,
  onOpenChange,
  onCaptured
}) => {
  const [search, setSearch] = useState("");

  const { data: caseBriefs = [], isLoading } = useQuery<CaseBriefPickerItem[]>({
    queryKey: ["prompt-test-case-briefs", search],
    queryFn: () => listCaseBriefsForCapture(search || undefined),
    enabled: open
  });

  const capture = useMutation({
    mutationFn: (caseBriefId: number) => captureTestCase({ caseBriefId }),
    onSuccess: (result) => {
      toast.success(
        result.reused
          ? `Reused existing version ${result.version} (${formatInputCount(result.turnCount)})`
          : `Captured version ${result.version} (${formatInputCount(result.turnCount)})`
      );
      onCaptured();
      onOpenChange(false);
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      toast.error(error?.response?.data?.message ?? "Failed to capture the case brief")
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Capture a case brief as a test case</DialogTitle>
        </DialogHeader>
        <Input placeholder="Search case briefs..." value={search} onChange={(e) => setSearch(e.target.value)} />
        <div className="mt-2 max-h-[420px] overflow-y-auto divide-y">
          {isLoading ? (
            <AppLoader />
          ) : caseBriefs.length === 0 ? (
            <div className="py-6 text-center text-gray-500">No case briefs found</div>
          ) : (
            caseBriefs.map((cb) => (
              <button
                key={cb.id}
                type="button"
                disabled={cb.turnCount === 0 || capture.isPending}
                onClick={() => capture.mutate(cb.id)}
                className="flex w-full items-center justify-between gap-4 px-2 py-3 text-left hover:bg-gray-50 disabled:opacity-50"
              >
                <div className="min-w-0">
                  <div className="truncate text-[14px] text-gray-900">{cb.name ?? `Case brief ${cb.id}`}</div>
                  <div className="truncate text-[12px] text-gray-500">{cb.organizationName ?? "—"}</div>
                  <div className="text-[12px] text-gray-400">
                    #{cb.id} · {formatInputCount(cb.turnCount)}
                  </div>
                </div>
                <span className="shrink-0 text-[12px] text-gray-400">{new Date(cb.updatedAt).toLocaleDateString()}</span>
              </button>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ORIGIN_LABELS: Record<string, string> = {
  case_brief_capture: "Captured",
  deficiency: "From deficiency"
};

export const TestCasesPage: FC = () => {
  const { featureKey = "" } = useParams<{ featureKey: string }>();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);

  const fromRevisionId = Number(searchParams.get("fromRevision")) || null;

  const { data: testCases, isLoading, isError } = useQuery<TestCaseListItem[]>({
    queryKey: ["prompt-test-cases"],
    queryFn: listTestCases
  });

  const renderList = () => {
    if (isLoading) return <AppLoader />;
    if (isError || !testCases) return <div className="py-6 text-center text-red-600">Failed to load test cases.</div>;
    if (testCases.length === 0) {
      return <div className="py-6 text-center text-gray-500">No test cases yet — capture one from a case brief.</div>;
    }
    return (
      <div className="divide-y rounded-lg border bg-card">
        {testCases.map((tc) => (
          <div key={tc.id} className="flex items-start justify-between gap-4 px-6 py-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <FigmaTypography component="span" variant="body-2" className="text-gray-900">
                  {tc.name}
                </FigmaTypography>
                <span className="rounded bg-gray-100 px-2 py-0.5 text-[12px] leading-[18px] text-gray-600">
                  {ORIGIN_LABELS[tc.origin] ?? tc.origin}
                </span>
              </div>
              <FigmaTypography component="div" variant="body-3" className="text-gray-400 mt-0.5">
                {formatInputCount(tc.turnCount)} · {tc.versionCount} version{tc.versionCount === 1 ? "" : "s"}
                {tc.sourceCaseBriefId ? ` · from case brief ${tc.sourceCaseBriefId}` : ""}
              </FigmaTypography>
            </div>
            <span className="shrink-0 text-[12px] leading-[18px] text-gray-400">
              {new Date(tc.createdAt).toLocaleDateString()}
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="container mx-auto py-8 max-w-4xl">
      <div className="mb-6">
        <Link
          to={fromRevisionId ? getRoutePromptsRevision(featureKey, fromRevisionId) : getRoutePromptsFeature(featureKey)}
          className="text-[14px] text-gray-500 hover:text-gray-700"
        >
          ← Back to {fromRevisionId ? "the revision" : featureKey}
        </Link>
        <div className="mt-2 flex items-center justify-between gap-4">
          <FigmaTypography component="h1" variant="heading-1">
            Test cases
          </FigmaTypography>
          <AppButton onClick={() => setDialogOpen(true)}>Capture from case brief</AppButton>
        </div>
        <FigmaTypography component="div" variant="body-3" className="text-gray-500 mt-1">
          A test case is a frozen set of user inputs. Replay it against a revision from the revision page to compare
          before and after.
        </FigmaTypography>
      </div>

      {renderList()}

      <CaptureDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onCaptured={() => queryClient.invalidateQueries({ queryKey: ["prompt-test-cases"] })}
      />
    </div>
  );
};
